import React, { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Select,
  Checkbox,
  RangeSlider,
  Divider,
  Spinner,
  SkeletonBodyText,
  SkeletonDisplayText,
  Banner,
  Box,
  ChoiceList,
  Badge,
} from "@shopify/polaris";
import AgeGatePreview from "../../components/settings/AgeGatePreview";
import { cloneDefaultConfig, DEFAULT_POPUP_BACKGROUND_OPACITY, DEFAULT_POPUP_OUTER_OPACITY } from "../../settings/defaultFormState";
import {
  COUNTRY_OPTIONS_WITH_ALL,
  LANGUAGE_OPTIONS_WITH_ALL,
  COUNTRY_CODE_ALL,
  LANGUAGE_CODE_ALL,
  normalizeLanguageCode,
} from "../../settings/countryLanguageOptions";
import { POPUP_LAYOUT_OPTIONS, normalizePopupLayout } from "../../settings/popupLayout";
import { mergeLayoutThemedPopup } from "../../settings/popupLayoutTheme";
import {
  POPUP_VERIFICATION_METHOD_CHOICES,
  DATE_FORMAT_TYPE_OPTIONS,
  DEFAULT_POPUP_CHECKBOX_LABEL,
  normalizePopupVerificationMethod,
  normalizeCheckoutVerificationMethod,
  coerceDateFormatType,
  dateOrderCommaFromDateFormat,
  resolveTemplateMinAge,
  applyTemplateMinAge,
} from "../../settings/verificationOptions";
import {
  fetchTemplate,
  fetchTemplates,
  createTemplate,
  updateTemplate,
} from "../../api/templatesApi";
import { uploadImageToShopify } from "../../api/shopifyFileUpload";
import { normalizeDisplayCriteria } from "../../settings/displayCriteria";
import { normalizeCheckoutTarget } from "../../settings/checkoutTarget";
import DisplayConditionSection from "../../components/settings/DisplayConditionSection";
import CheckoutTargetSection from "../../components/settings/CheckoutTargetSection";
import PrivacyPolicyRichTextEditor from "../../components/settings/PrivacyPolicyRichTextEditor";
import { LabelWithTooltip } from "../../components/settings/LabelWithTooltip";
import { getShopifyAdminCheckoutEditorUrl } from "../../utils/shopDomain";
import {
  formatShopPlanLabel,
  isDevelopmentShopPlan,
  isShopifyPlusPlan,
} from "../../utils/shopifyPlan";
import { useDispatch, useSelector } from "react-redux";
import { checkInstall } from "../../store/slices/installSlice";
import { SaveBar, useAppBridge } from "@shopify/app-bridge-react";
import { createOrUpdateAgeTemplateShopMetafield } from "../../helper/ageTemplateMetafield";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";

const TEMPLATE_SAVE_BAR_ID = "template-editor-save-bar";

const DEFAULT_FONT_STACK = "'Inter', sans-serif";

const FONT_FAMILY_OPTIONS = [
  { label: "Sans", value: "Arial, sans-serif" },
  { label: "Serif", value: "'Source Serif Pro', Georgia, serif" },
  { label: "Monospace", value: "'Source Code Pro', 'Courier New', monospace" },
  { label: "Inter", value: "'Inter', sans-serif" },
  { label: "Poppins", value: "'Poppins', sans-serif" },
  { label: "Roboto", value: "'Roboto', sans-serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
  { label: "Lato", value: "'Lato', sans-serif" },
  { label: "Merriweather", value: "'Merriweather', serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
];

const LEGACY_FONT_VALUE_MAP = {
  Inter: "'Inter', sans-serif",
  "system-ui": "Arial, sans-serif",
  Georgia: "'Source Serif Pro', Georgia, serif",
  "ui-monospace": "'Source Code Pro', 'Courier New', monospace",
};

/** Undo accidental HTML escaping in stored font stacks (e.g. &#039; instead of '). */
function decodeHtmlEntitiesString(input) {
  let s = String(input ?? "").trim();
  if (!s) return "";
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    for (let i = 0; i < 6; i++) {
      ta.innerHTML = s;
      const next = String(ta.value).trim();
      if (next === s) break;
      s = next;
    }
    return s;
  }
  for (let i = 0; i < 6; i++) {
    const next = s
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#x22;/gi, '"')
      .replace(/&#0*34;/g, '"')
      .replace(/&#x27;/gi, "'")
      .replace(/&#0*39;/g, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&amp;/g, "&");
    if (next === s) break;
    s = next;
  }
  return s.trim();
}

/** Normalize font fields on popup (title / description / buttons) after API load or before save. */
function sanitizeFontsInPopup(popup) {
  if (!popup || typeof popup !== "object") return popup;
  const decode = (v) => (v == null || v === "" ? v : decodeHtmlEntitiesString(String(v)));
  const p = { ...popup };
  if (p.text && typeof p.text === "object") {
    p.text = { ...p.text };
    if (p.text.title && typeof p.text.title === "object") {
      p.text.title = { ...p.text.title, font: decode(p.text.title.font) ?? p.text.title.font };
    }
    if (p.text.description && typeof p.text.description === "object") {
      p.text.description = {
        ...p.text.description,
        font: decode(p.text.description.font) ?? p.text.description.font,
      };
    }
  }
  if (p.buttons && typeof p.buttons === "object") {
    p.buttons = { ...p.buttons };
    if (p.buttons.accept && typeof p.buttons.accept === "object") {
      p.buttons.accept = { ...p.buttons.accept, font: decode(p.buttons.accept.font) ?? p.buttons.accept.font };
    }
    if (p.buttons.reject && typeof p.buttons.reject === "object") {
      p.buttons.reject = { ...p.buttons.reject, font: decode(p.buttons.reject.font) ?? p.buttons.reject.font };
    }
  }
  if (p.confirmCheckbox && typeof p.confirmCheckbox === "object") {
    p.confirmCheckbox = {
      ...p.confirmCheckbox,
      font: decode(p.confirmCheckbox.font) ?? p.confirmCheckbox.font,
    };
  }
  return p;
}

function fontKeyForMatch(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s*,\s*/g, ",")
    .toLowerCase();
}

/**
 * Maps stored font (DB / metafield) to the canonical option value, or keeps a custom stack.
 * Never silently drops a saved value — unknown stacks stay as-is so reload + save stay stable.
 */
function resolveFontSelectStored(stored) {
  const raw = decodeHtmlEntitiesString(String(stored ?? ""));
  if (!raw) return DEFAULT_FONT_STACK;
  const exact = FONT_FAMILY_OPTIONS.find((o) => o.value === raw);
  if (exact) return exact.value;
  const fk = fontKeyForMatch(raw);
  const byCanon = FONT_FAMILY_OPTIONS.find((o) => fontKeyForMatch(o.value) === fk);
  if (byCanon) return byCanon.value;
  const mapped = LEGACY_FONT_VALUE_MAP[raw];
  if (mapped) {
    const mHit = FONT_FAMILY_OPTIONS.find((o) => o.value === mapped);
    if (mHit) return mHit.value;
    return mapped;
  }
  const legacyKey = Object.keys(LEGACY_FONT_VALUE_MAP).find((k) => fontKeyForMatch(k) === fk);
  if (legacyKey) return LEGACY_FONT_VALUE_MAP[legacyKey];
  return raw;
}

function fontSelectOptions(stored) {
  const v = resolveFontSelectStored(stored);
  if (FONT_FAMILY_OPTIONS.some((o) => o.value === v)) return FONT_FAMILY_OPTIONS;
  /* Avoid putting raw font-family in the label (quotes / entities confuse Polaris display). */
  return [{ label: "Custom font", value: v }, ...FONT_FAMILY_OPTIONS];
}

function normalizeFontSelectValue(stored) {
  return resolveFontSelectStored(stored);
}

function parsePxSize(value, fallback = 16) {
  if (value == null || value === "") return fallback;
  const n = parseInt(String(value).replace(/px\s*$/i, "").trim(), 10);
  return Number.isFinite(n) ? n : fallback;
}

function formatPxSize(n) {
  const num = Number(n);
  const v = Number.isFinite(num) ? Math.min(200, Math.max(8, Math.round(num))) : 16;
  return `${v}px`;
}

function safeHexForPicker(hex) {
  const s = String(hex ?? "").trim();
  const m = s.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m) return "#000000";
  let h = m[1];
  if (h.length === 3) {
    h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  return `#${h}`.toUpperCase();
}

function ColorHexField({ label, value, onChange, helpText, disabled }) {
  const propStr = String(value ?? "").trim();
  const [draft, setDraft] = useState(propStr);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const colorInputRef = useRef(null);
  const previewRafRef = useRef(null);

  useEffect(() => {
    setDraft(propStr);
  }, [propStr]);

  // During native color UI drag: update local draft immediately; push to parent at most once per
  // animation frame so the preview stays live without hundreds of full-editor re-renders per second.
  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return undefined;
    const flushPreview = () => {
      previewRafRef.current = null;
      const input = colorInputRef.current;
      if (!input) return;
      onChangeRef.current(input.value.toUpperCase());
    };
    const schedulePreview = () => {
      if (previewRafRef.current != null) cancelAnimationFrame(previewRafRef.current);
      previewRafRef.current = requestAnimationFrame(flushPreview);
    };
    const onNativeInput = () => {
      const hex = el.value.toUpperCase();
      setDraft(hex);
      schedulePreview();
    };
    const commitToParent = () => {
      if (previewRafRef.current != null) {
        cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
      const hex = el.value.toUpperCase();
      setDraft(hex);
      onChangeRef.current(hex);
    };
    el.addEventListener("input", onNativeInput);
    el.addEventListener("change", commitToParent);
    el.addEventListener("blur", commitToParent);
    return () => {
      if (previewRafRef.current != null) cancelAnimationFrame(previewRafRef.current);
      previewRafRef.current = null;
      el.removeEventListener("input", onNativeInput);
      el.removeEventListener("change", commitToParent);
      el.removeEventListener("blur", commitToParent);
    };
  }, []);

  const pickerVal = safeHexForPicker(draft);

  return (
    <TextField
      label={helpText ? <LabelWithTooltip label={label} tooltip={helpText} /> : label}
      value={draft}
      onChange={(v) => {
        setDraft(v);
        onChange(v);
      }}
      autoComplete="off"
      disabled={disabled}
      connectedRight={
        <div style={{ display: "flex", alignItems: "center", paddingInline: "10px" }}>
          <label
            style={{
              position: "relative",
              width: 28,
              height: 28,
              borderRadius: 6,
              overflow: "hidden",
              cursor: "pointer",
              flexShrink: 0,
            }}
            title="Pick color"
          >
            <span
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: pickerVal,
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 6,
                pointerEvents: "none",
              }}
            />
            <input
              ref={colorInputRef}
              type="color"
              value={pickerVal}
              disabled={disabled}
              style={{
                position: "absolute",
                opacity: 0,
                width: "160%",
                height: "160%",
                cursor: "pointer",
                left: "-30%",
                top: "-30%",
              }}
            />
          </label>
        </div>
      }
    />
  );
}

function FontSizePxField({ label, value, onChange, min = 8, max = 200, tooltip, disabled }) {
  const n = parsePxSize(value, 16);
  return (
    <TextField
      label={tooltip ? <LabelWithTooltip label={label} tooltip={tooltip} /> : label}
      type="number"
      min={min}
      max={max}
      suffix="Px"
      value={String(n)}
      onChange={(v) => {
        if (v === "") {
          onChange(formatPxSize(16));
          return;
        }
        const parsed = parseInt(v, 10);
        if (!Number.isFinite(parsed)) return;
        onChange(formatPxSize(parsed));
      }}
      autoComplete="off"
      disabled={disabled}
    />
  );
}

const TEXT_WEIGHT_OPTIONS = [
  { label: "Regular", value: "400" },
  { label: "Medium", value: "500" },
  { label: "Semibold", value: "600" },
  { label: "Bold", value: "700" },
];

const ANIMATIONS = [
  { label: "None", value: "none" },
  { label: "Fade In", value: "fade_in" },
  { label: "Fade In Top Left", value: "fade_in_top_left" },
  { label: "Fade In Top Right", value: "fade_in_top_right" },
  { label: "Fade In Bottom Left", value: "fade_in_bottom_left" },
  { label: "Fade In Bottom Right", value: "fade_in_bottom_right" },
  { label: "Slide In Up", value: "slide_in_up" },
  { label: "Slide In Down", value: "slide_in_down" },
  { label: "Slide In Left", value: "slide_in_left" },
  { label: "Slide In Right", value: "slide_in_right" },
  { label: "Zoom In", value: "zoom_in" },
  { label: "Pulse", value: "pulse" },
  { label: "Flip", value: "flip" },
  { label: "Bounce", value: "bounce" },
  { label: "Bounce In Left", value: "bounce_in_left" },
  { label: "Bounce In Right", value: "bounce_in_right" },
  { label: "Swing", value: "swing" },
  { label: "Tada", value: "tada" },
  { label: "Shake", value: "shake" },
  { label: "Rubber Band", value: "rubber_band" },
  { label: "Wobble", value: "wobble" },
  { label: "Jello", value: "jello" },
  { label: "Wave", value: "wave" },
  { label: "Spin In", value: "spin_in" },
  { label: "Jack In The Box", value: "jack_in_the_box" },
  { label: "Roll In Top", value: "roll_in_top" },
  { label: "Roll In Bottom", value: "roll_in_bottom" },
  { label: "Perspective Pop", value: "perspective_pop" },
  { label: "Skew Pop", value: "skew_pop" },
  { label: "Light Speed In Left", value: "light_speed_in_left" },
  { label: "Light Speed In Right", value: "light_speed_in_right" },
  { label: "Rotate In Left", value: "rotate_in_left" },
  { label: "Rotate In Right", value: "rotate_in_right" },
  { label: "Jiggle In Left", value: "jiggle_in_left" },
  { label: "Jiggle In Right", value: "jiggle_in_right" },
];

const ANIMATION_VALUE_SET = new Set(ANIMATIONS.map((a) => a.value));

function normalizePopupAnimation(v) {
  if (v === "fade") return "fade_in";
  if (v === "slide") return "slide_in_up";
  if (v === "zoom") return "zoom_in";
  return ANIMATION_VALUE_SET.has(v) ? v : "none";
}

function mergePopup(popup, patch) {
  return { ...popup, ...patch };
}

/** Deep-merge nested popup slices so a partial API `text`/`buttons` never wipes fonts saved earlier. */
function mergeAgePopupFromApi(defPopup, apiPatch) {
  if (!apiPatch || typeof apiPatch !== "object") return { ...defPopup };
  const p = apiPatch;
  return {
    ...defPopup,
    ...p,
    text: {
      ...defPopup.text,
      ...p.text,
      title: { ...defPopup.text?.title, ...p.text?.title },
      description: { ...defPopup.text?.description, ...p.text?.description },
    },
    buttons: {
      ...defPopup.buttons,
      ...p.buttons,
      accept: { ...defPopup.buttons?.accept, ...p.buttons?.accept },
      reject: { ...defPopup.buttons?.reject, ...p.buttons?.reject },
    },
    popupStyle: { ...defPopup.popupStyle, ...p.popupStyle },
    dob: { ...defPopup.dob, ...p.dob },
    confirmCheckbox: {
      ...defPopup.confirmCheckbox,
      ...p.confirmCheckbox,
      label:
        p.confirmCheckbox != null && String(p.confirmCheckbox.label ?? "").trim() !== ""
          ? String(p.confirmCheckbox.label).trim()
          : defPopup.confirmCheckbox.label,
      font: p.confirmCheckbox?.font ?? defPopup.confirmCheckbox.font,
      weight: p.confirmCheckbox?.weight ?? defPopup.confirmCheckbox.weight,
      size: p.confirmCheckbox?.size ?? defPopup.confirmCheckbox.size,
      color: p.confirmCheckbox?.color ?? defPopup.confirmCheckbox.color,
    },
    popupShow: { ...defPopup.popupShow, ...p.popupShow },
    logo: { ...defPopup.logo, ...p.logo },
    displayCriteria: p.displayCriteria
      ? normalizeDisplayCriteria({ ...defPopup.displayCriteria, ...p.displayCriteria })
      : defPopup.displayCriteria,
    privacy: { ...defPopup.privacy, ...p.privacy },
    advanced: { ...defPopup.advanced, ...p.advanced },
    restrictVerification: p.restrictVerification ?? defPopup.restrictVerification,
  };
}

function mergeAgeCheckoutFromApi(defCheckout, apiPatch) {
  if (!apiPatch || typeof apiPatch !== "object") return { ...defCheckout };
  const c = apiPatch;
  return {
    ...defCheckout,
    ...c,
    dob: { ...defCheckout.dob, ...c.dob },
    confirmCheckbox: {
      ...defCheckout.confirmCheckbox,
      ...c.confirmCheckbox,
      label:
        c.confirmCheckbox != null && String(c.confirmCheckbox.label ?? "").trim() !== ""
          ? String(c.confirmCheckbox.label).trim()
          : defCheckout.confirmCheckbox.label,
      font: c.confirmCheckbox?.font ?? defCheckout.confirmCheckbox.font,
      weight: c.confirmCheckbox?.weight ?? defCheckout.confirmCheckbox.weight,
      size: c.confirmCheckbox?.size ?? defCheckout.confirmCheckbox.size,
      color: c.confirmCheckbox?.color ?? defCheckout.confirmCheckbox.color,
    },
    ageRule: { ...defCheckout.ageRule, ...c.ageRule },
    target: normalizeCheckoutTarget({ ...defCheckout.target, ...c.target }),
  };
}

function cookieDurationSelectOptions(savedDays) {
  const standard = [1, 3, 7, 14, 30, 60, 90, 180];
  const d = Number(savedDays);
  const hasCustom = Number.isFinite(d) && d > 0 && !standard.includes(d);
  const head = hasCustom ? [{ label: `${d} days (current)`, value: String(d) }] : [];
  return [
    ...head,
    ...standard.map((n) => ({ label: `${n} days`, value: String(n) })),
  ];
}

const IMAGE_MAX_BYTES = 4 * 1024 * 1024;

function ImageUploadField({ label, helpText, value, onChange, uploadAlt = "", disabled }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const onFileSelected = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setUploadError("Please choose an image file (PNG, JPG, SVG, WEBP, etc).");
        return;
      }
      if (file.size > IMAGE_MAX_BYTES) {
        setUploadError("Image size must be 4MB or less.");
        return;
      }
      setUploadError("");
      setUploading(true);
      try {
        const { url } = await uploadImageToShopify(file, uploadAlt || label || "");
        onChange(url);
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : "Unable to upload image.");
      } finally {
        setUploading(false);
      }
    },
    [onChange, uploadAlt, label],
  );

  return (
    <BlockStack gap="200">
      <Text as="p" variant="bodyMd" fontWeight="medium">
        {helpText ? <LabelWithTooltip label={label} tooltip={helpText} /> : label}
      </Text>
      {value ? (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: 8,
            background: "#fafafa",
            maxWidth: 260,
          }}
        >
          <img
            src={value}
            alt=""
            style={{ width: "100%", maxHeight: 120, objectFit: "contain", display: "block" }}
          />
        </div>
      ) : null}
      <InlineStack gap="200">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onFileSelected}
          disabled={uploading || disabled}
        />
        <Button
          type="button"
          disabled={uploading || disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          {value ? "Replace image" : "Upload image"}
        </Button>
        {value ? (
          <Button tone="critical" type="button" onClick={() => onChange("")}>
            Remove
          </Button>
        ) : null}
      </InlineStack>
      {uploading ? (
        <Text as="p" variant="bodySm" tone="subdued">
          Uploading image...
        </Text>
      ) : null}
      {uploadError ? (
        <Banner tone="critical">
          <p>{uploadError}</p>
        </Banner>
      ) : null}
    </BlockStack>
  );
}

function buildTemplatePersistPayload(meta, config) {
  const synced = applyTemplateMinAge(config, resolveTemplateMinAge(config));
  const popup = sanitizeFontsInPopup({ ...(synced.popup || {}) });
  return {
    name: meta.name,
    country_code: meta.country_code,
    language_code: meta.language_code,
    config: {
      ...synced,
      popup: {
        ...popup,
        layout: normalizePopupLayout(popup.layout ?? synced.popup?.layout),
        verificationMethod: normalizePopupVerificationMethod(
          popup.verificationMethod ?? synced.popup?.verificationMethod,
        ),
      },
      checkout: {
        ...synced.checkout,
        layout: normalizePopupLayout(synced.checkout?.layout),
        verificationMethod: normalizeCheckoutVerificationMethod(synced.checkout?.verificationMethod),
      },
    },
  };
}

export default function TemplateEditorPage() {
  const { templateId } = useParams();
  const isNewDraft = templateId === "new";
  const rrNavigate = useNavigate();
  const navigate = useCallback(
    (to, options) => {
      startTransition(() => {
        rrNavigate(to, options);
      });
    },
    [rrNavigate],
  );
  const dispatch = useDispatch();
  const shopify = useAppBridge();
  const hideTemplateSaveBar = useCallback(async () => {
    try {
      await shopify?.saveBar?.hide?.(TEMPLATE_SAVE_BAR_ID);
    } catch {
      /* App Bridge optional */
    }
  }, [shopify]);
  const navigateToSettingsList = useCallback(async () => {
    await hideTemplateSaveBar();
    navigate("/settings", { replace: true });
  }, [hideTemplateSaveBar, navigate]);
  const installData = useSelector((state) => state.install.installData);
  const settingsPaneRef = useRef(null);
  const [loading, setLoading] = useState(() => !isNewDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [baselineJson, setBaselineJson] = useState(null);
  const [metafieldWarning, setMetafieldWarning] = useState(null);
  const [tab, setTab] = useState(0);
  /** Checkout preview only: mimic blocked buyer journey (top banner + highlighted card). */
  const [checkoutPreviewValidationError, setCheckoutPreviewValidationError] = useState(false);
  const [meta, setMeta] = useState({ name: "", country_code: "", language_code: "", is_primary: false });
  const [config, setConfig] = useState(() => cloneDefaultConfig());
  /** Set after a failed create save — highlights country/language fields. */
  const [metaSaveValidationActive, setMetaSaveValidationActive] = useState(false);
  /** Other templates (for duplicate country+language check on new draft). */
  const [existingTemplates, setExistingTemplates] = useState([]);

  const setPopup = useCallback((fnOrPatch) => {
    setConfig((prev) => ({
      ...prev,
      popup: typeof fnOrPatch === "function" ? fnOrPatch(prev.popup) : mergePopup(prev.popup, fnOrPatch),
    }));
  }, []);

  const setCheckout = useCallback((fnOrPatch) => {
    setConfig((prev) => ({
      ...prev,
      checkout:
        typeof fnOrPatch === "function" ? fnOrPatch(prev.checkout) : { ...prev.checkout, ...fnOrPatch },
    }));
  }, []);

  useEffect(() => {
    if (!templateId) {
      void navigateToSettingsList();
      return;
    }
    if (templateId === "new") {
      setError(null);
      setMetafieldWarning(null);
      setBaselineJson(null);
      const def = cloneDefaultConfig();
      const nextMeta = {
        name: "New template",
        country_code: COUNTRY_CODE_ALL,
        language_code: LANGUAGE_CODE_ALL,
        is_primary: false,
      };
      setMeta(nextMeta);
      setConfig(def);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setBaselineJson(null);
      setMetafieldWarning(null);
      try {
        const res = await fetchTemplate(templateId);
        const t = res?.data?.template;
        if (!t) throw new Error("Template not found");
        if (cancelled) return;
        const nextMeta = {
          name: t.name || "",
          country_code: String(t.country_code || COUNTRY_CODE_ALL)
            .toUpperCase()
            .slice(0, 8),
          language_code: normalizeLanguageCode(t.language_code || LANGUAGE_CODE_ALL) || LANGUAGE_CODE_ALL,
          is_primary: !!t.is_primary,
        };
        const def = cloneDefaultConfig();
        const tc = t.config || {};
        const mergedPopup = sanitizeFontsInPopup(mergeAgePopupFromApi(def.popup, tc.popup || {}));
        const mpDob = mergedPopup.dob || {};
        const popupDf = coerceDateFormatType(mpDob.dateFormat ?? def.popup.dob.dateFormat);
        mergedPopup.dob = {
          ...def.popup.dob,
          ...mpDob,
          dateFormat: popupDf,
          dateOrder: dateOrderCommaFromDateFormat(popupDf),
        };
        const mergedCheckout = mergeAgeCheckoutFromApi(def.checkout, tc.checkout || {});
        const tcDob = mergedCheckout.dob || {};
        const mergedDf = coerceDateFormatType(tcDob.dateFormat ?? def.checkout.dob.dateFormat);
        mergedCheckout.dob = {
          ...def.checkout.dob,
          ...tcDob,
          dateFormat: mergedDf,
          dateOrder: dateOrderCommaFromDateFormat(mergedDf),
        };
        mergedCheckout.verificationMethod = normalizeCheckoutVerificationMethod(
          mergedCheckout.verificationMethod,
        );
        mergedCheckout.layout = normalizePopupLayout(mergedCheckout.layout);
        mergedCheckout.target = normalizeCheckoutTarget({
          ...def.checkout.target,
          ...(mergedCheckout.target || {}),
        });
        mergedCheckout.ageVerificationBlockEnabled = !!mergedCheckout.ageVerificationBlockEnabled;
        const nextConfig = applyTemplateMinAge(
          {
            ...def,
            ...tc,
            popup: mergedPopup,
            checkout: mergedCheckout,
          },
          resolveTemplateMinAge({ ...def, ...tc, popup: mergedPopup, checkout: mergedCheckout }),
        );
        setMeta(nextMeta);
        setConfig(nextConfig);
        setBaselineJson(JSON.stringify({ meta: nextMeta, config: nextConfig }));
      } catch (e) {
        if (!cancelled) {
          const msg =
            typeof e === "string" ? e : e instanceof Error ? e.message : e?.message || "Load failed";
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId, navigate, navigateToSettingsList]);

  useEffect(() => {
    if (templateId !== "new") {
      setExistingTemplates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTemplates();
        const list = res?.data?.templates ?? [];
        if (!cancelled) setExistingTemplates(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setExistingTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const templatePairKey = useCallback((countryCode, languageCode) => {
    const cc = String(countryCode ?? "")
      .trim()
      .toUpperCase()
      .slice(0, 8);
    const lc = normalizeLanguageCode(languageCode ?? "");
    if (!cc || !lc) return null;
    return `${cc}|${lc}`;
  }, []);

  const countryLanguageDuplicate = useMemo(() => {
    if (!isNewDraft || !existingTemplates.length) return false;
    const key = templatePairKey(meta.country_code, meta.language_code);
    if (!key) return false;
    return existingTemplates.some((t) => templatePairKey(t.country_code, t.language_code) === key);
  }, [isNewDraft, existingTemplates, meta.country_code, meta.language_code, templatePairKey]);

  const newTemplateMetaIncomplete = useMemo(
    () =>
      isNewDraft &&
      (!String(meta.country_code ?? "").trim() || !String(meta.language_code ?? "").trim()),
    [isNewDraft, meta.country_code, meta.language_code],
  );

  const isDirty = useMemo(() => {
    if (loading) return false;
    if (isNewDraft) return true;
    if (baselineJson == null) return false;
    try {
      return baselineJson !== JSON.stringify({ meta, config });
    } catch {
      return true;
    }
  }, [loading, isNewDraft, baselineJson, meta, config]);

  const persistMetafieldForTemplate = useCallback(
    async (numericTemplateId, payload) => {
      let shopifyId = installData?.shop_info?.shopify_id;
      if (shopifyId == null || String(shopifyId).trim() === "") {
        try {
          const refreshed = await dispatch(checkInstall(true)).unwrap();
          shopifyId = refreshed?.data?.shop_info?.shopify_id;
        } catch {
          /* install refresh failed — metafields skipped */
        }
      }
      if (shopifyId != null && String(shopifyId).trim() !== "") {
        try {
          let allTemplatesDetailed = null;
          try {
            const listRes = await fetchTemplates();
            const rows = listRes?.data?.templates ?? [];
            const loaded = await Promise.all(
              rows.map(async (row) => {
                const tr = await fetchTemplate(row.id);
                return tr?.data?.template ?? null;
              }),
            );
            allTemplatesDetailed = loaded.filter(Boolean);
          } catch {
            allTemplatesDetailed = null;
          }
          await createOrUpdateAgeTemplateShopMetafield(
            shopifyId,
            numericTemplateId,
            payload,
            allTemplatesDetailed && allTemplatesDetailed.length ? allTemplatesDetailed : null,
          );
        } catch (mfErr) {
          const mfMsg =
            typeof mfErr === "string"
              ? mfErr
              : mfErr instanceof Error
                ? mfErr.message
                : mfErr?.message || "Metafield update failed";
          setMetafieldWarning(
            `Saved to the app database, but the Shopify shop metafield was not updated: ${mfMsg}`,
          );
        }
      } else {
        setMetafieldWarning(
          "Saved to the app database. Shopify shop numeric id (shopify_id) is missing — shop metafields were not updated. Re-open the app from Shopify Admin after backend can read `shop { id }`, or set clients.shopify_id in the database.",
        );
      }
    },
    [installData, dispatch],
  );

  const onSave = useCallback(async () => {
    if (isNewDraft && newTemplateMetaIncomplete) {
      setMetaSaveValidationActive(true);
      setError("Select a country and a language before creating this template.");
      settingsPaneRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return;
    }
    if (isNewDraft && countryLanguageDuplicate) {
      setError(
        "A template already exists for this country and language. Change country or language, or edit the existing template from the list.",
      );
      settingsPaneRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return;
    }
    setMetaSaveValidationActive(false);
    setSaving(true);
    setError(null);
    setMetafieldWarning(null);
    const payload = buildTemplatePersistPayload(meta, config);
    try {
      if (templateId === "new") {
        const created = await createTemplate(payload);
        const newId = created?.data?.id;
        if (newId == null) throw new Error("Create did not return a template id");
        await persistMetafieldForTemplate(String(newId), payload);
        try {
          shopify?.toast?.show?.("Template created");
        } catch {
          /* toast optional */
        }
        await hideTemplateSaveBar();
        navigate(`/settings/templates/${newId}`, { replace: true });
      } else {
        await updateTemplate(templateId, payload);
        await persistMetafieldForTemplate(templateId, payload);
        setBaselineJson(JSON.stringify({ meta, config }));
        try {
          shopify?.toast?.show?.("Template saved");
        } catch {
          /* toast may be unavailable outside embedded admin */
        }
        await hideTemplateSaveBar();
      }
    } catch (e) {
      const msg =
        typeof e === "string" ? e : e instanceof Error ? e.message : e?.message || "Save failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [
    templateId,
    isNewDraft,
    countryLanguageDuplicate,
    newTemplateMetaIncomplete,
    meta,
    config,
    shopify,
    navigate,
    hideTemplateSaveBar,
    persistMetafieldForTemplate,
  ]);

  const onDiscard = useCallback(() => {
    if (templateId === "new") {
      void navigateToSettingsList();
      return;
    }
    if (!baselineJson) return;
    try {
      const parsed = JSON.parse(baselineJson);
      setMeta(parsed.meta);
      setConfig(parsed.config);
      setError(null);
      setMetafieldWarning(null);
    } catch {
      setError("Could not discard changes.");
    }
  }, [baselineJson, templateId, navigateToSettingsList]);

  const previewMode = tab === 0 ? "popup" : "checkout";

  const shopPlanStored = installData?.shop_info?.shop_plan ?? null;
  const checkoutDevCapable = useMemo(
    () => isDevelopmentShopPlan(shopPlanStored),
    [shopPlanStored],
  );
  /**
   * Checkout UI extensions need Plus (or dev/staff/sandbox-style plans). Lock checkout unless Plus or dev-capable —
   * including when `shop_plan` is missing (treat as non-Plus until install sync).
   */
  const showCheckoutPlanRestriction =
    !isShopifyPlusPlan(shopPlanStored) && !checkoutDevCapable;
  const shopPlanLabel = useMemo(() => formatShopPlanLabel(shopPlanStored), [shopPlanStored]);

  const countrySelectOptions = COUNTRY_OPTIONS_WITH_ALL;
  const languageSelectOptions = LANGUAGE_OPTIONS_WITH_ALL;

  const layoutGrid = useMemo(
    () => ({
      display: "grid",
      /* Settings column narrower than preview (~40% / ~60% of free space above mins). */
      gridTemplateColumns: "minmax(280px, 1fr) minmax(420px, 2fr)",
      gap: "20px",
      alignItems: "start",
    }),
    [],
  );

  const pageTitle = meta.name || (isNewDraft ? "New template" : "Template");

  useAppTitleBar({
    title: pageTitle,
    breadcrumb: { label: "Templates", onClick: () => void navigateToSettingsList() },
  });

  if (loading) {
    return (
      <Page fullWidth>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 1fr) minmax(420px, 2fr)",
            gap: "20px",
            alignItems: "start",
            minHeight: "min(72vh, 560px)",
          }}
        >
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={4} />
              <SkeletonBodyText lines={6} />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
          <Card>
            <Box padding="400" minHeight="360px">
              <InlineStack align="center" blockAlign="center">
                <Spinner accessibilityLabel="Loading template" />
              </InlineStack>
            </Box>
          </Card>
        </div>
      </Page>
    );
  }

  const p = config.popup || {};
  const ch = config.checkout || {};

  const saveBarOpen = !loading && (isNewDraft || (baselineJson != null && baselineJson !== JSON.stringify({ meta, config })));

  return (
    <Page fullWidth>
      <BlockStack gap="500">
        {error ? (
          <Banner tone="critical" title="Error" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        ) : null}
        {metafieldWarning ? (
          <Banner tone="warning" title="Metafield" onDismiss={() => setMetafieldWarning(null)}>
            {metafieldWarning}
          </Banner>
        ) : null}
        {isNewDraft && countryLanguageDuplicate ? (
          <Banner tone="critical" title="Duplicate country and language">
            <p>
              You already have a template for this country and language. Change one of them, or edit the existing
              template from the list.
            </p>
          </Banner>
        ) : null}

        <div style={layoutGrid}>
          <div
            ref={settingsPaneRef}
            className="section-scroll-pane section-scroll-pane--template-editor-left"
          >
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="400">
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      Template details
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Template name, country, and language.
                    </Text>
                  </BlockStack>
                  <TextField
                    label="Template name"
                    value={meta.name}
                    onChange={(v) => setMeta((m) => ({ ...m, name: v }))}
                    autoComplete="off"
                  />
                  <div className="age-template-meta-select-row">
                    <InlineStack gap="400" wrap={false}>
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <Select
                          label="Country"
                          options={countrySelectOptions}
                          value={meta.country_code}
                          error={
                            metaSaveValidationActive && !String(meta.country_code ?? "").trim()
                              ? "Select a country"
                              : undefined
                          }
                          onChange={(v) => {
                            setMetaSaveValidationActive(false);
                            setError(null);
                            setMeta((m) => ({ ...m, country_code: v }));
                          }}
                        />
                      </div>
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <Select
                          label="Language"
                          options={languageSelectOptions}
                          value={meta.language_code}
                          error={
                            metaSaveValidationActive && !String(meta.language_code ?? "").trim()
                              ? "Select a language"
                              : undefined
                          }
                          onChange={(v) => {
                            setMetaSaveValidationActive(false);
                            setError(null);
                            setMeta((m) => ({ ...m, language_code: v }));
                          }}
                        />
                      </div>
                    </InlineStack>
                  </div>
                  <Divider />
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Age rule
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Applies to both popup and checkout verification for this template.
                    </Text>
                    <TextField
                      label={
                        <LabelWithTooltip
                          label="Minimum age"
                          tooltip="Minimum age required to pass verification. Used in popup text, buttons, DOB checks, and checkout."
                        />
                      }
                      type="number"
                      min={1}
                      max={120}
                      value={String(resolveTemplateMinAge(config))}
                      onChange={(v) =>
                        setConfig((prev) =>
                          applyTemplateMinAge(
                            prev,
                            Math.min(120, Math.max(1, parseInt(v, 10) || 18)),
                          ),
                        )
                      }
                      autoComplete="off"
                    />
                  </BlockStack>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      Editor
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Popup or checkout verification.
                    </Text>
                  </BlockStack>
                  <div className="age-editor-main-tabs" role="tablist" aria-label="Editor">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 0}
                      id="age-editor-tab-popup"
                      className={`age-editor-main-tabs__btn${tab === 0 ? " age-editor-main-tabs__btn--active" : ""}`}
                      onClick={() => setTab(0)}
                    >
                      <span className="age-editor-main-tabs__btn-text">Popup settings</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 1}
                      id="age-editor-tab-checkout"
                      className={`age-editor-main-tabs__btn${tab === 1 ? " age-editor-main-tabs__btn--active" : ""}`}
                      onClick={() => setTab(1)}
                    >
                      <span className="age-editor-main-tabs__btn-text">Checkout verification</span>
                    </button>
                  </div>
                </BlockStack>
              </Card>

              {tab === 0 ? (
                <PopupSection
                  setPopup={setPopup}
                  p={p}
                  popupEnabled={!!config.popupEnabled}
                  setConfig={setConfig}
                />
              ) : (
                <CheckoutSection
                  config={config}
                  setConfig={setConfig}
                  setCheckout={setCheckout}
                  ch={ch}
                  shopPlanLabel={shopPlanLabel}
                  showCheckoutPlanRestriction={showCheckoutPlanRestriction}
                  checkoutPreviewValidationError={checkoutPreviewValidationError}
                  onCheckoutPreviewValidationChange={setCheckoutPreviewValidationError}
                />
              )}
            </BlockStack>
          </div>

          <div className="section-scroll-pane">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Live preview
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {tab === 0 ? "Popup modal" : "Checkout step"} — reflects current settings
                </Text>
                <AgeGatePreview
                  mode={previewMode}
                  config={config}
                  checkoutShowValidationError={checkoutPreviewValidationError}
                />
              </BlockStack>
            </Card>
          </div>
        </div>
      </BlockStack>

      <SaveBar
        id={TEMPLATE_SAVE_BAR_ID}
        open={saveBarOpen}
        discardConfirmation={!isNewDraft}
      >
        <button
          type="button"
          variant="primary"
          loading={saving ? "" : undefined}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void onSave();
          }}
          disabled={saving || !saveBarOpen}
        >
          {isNewDraft ? "Create template" : "Save"}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDiscard();
          }}
          disabled={saving || !saveBarOpen}
        >
          {isNewDraft ? "Cancel" : "Discard"}
        </button>
      </SaveBar>
    </Page>
  );
}

const POPUP_EDITOR_SUB_TABS = [
  { id: "info", label: "Info" },
  { id: "background", label: "Background" },
  { id: "text", label: "Text" },
  { id: "button", label: "Button" },
  { id: "css", label: "CSS" },
];

function PopupAccordionInfoBody({ p, setPopup }) {
  return (
    <BlockStack gap="400">
      <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Popup layout & verification
          </Text>
          <Select
            label="Popup layout"
            options={POPUP_LAYOUT_OPTIONS}
            value={normalizePopupLayout(p.layout)}
            onChange={(v) => setPopup((prev) => mergeLayoutThemedPopup(prev, v))}
          />
          <ChoiceList
            title="Method"
            choices={POPUP_VERIFICATION_METHOD_CHOICES}
            selected={[normalizePopupVerificationMethod(p.verificationMethod)]}
            onChange={(selected) => {
              const next = selected[0];
              setPopup((prev) => {
                const out = { ...prev, verificationMethod: next };
                if (next === "checkbox") {
                  const defCb = cloneDefaultConfig().popup.confirmCheckbox;
                  const prevCb = prev.confirmCheckbox || {};
                  out.confirmCheckbox = {
                    ...defCb,
                    ...prevCb,
                    enabled: true,
                    label:
                      (prevCb.label && String(prevCb.label).trim()) ||
                      defCb.label,
                    font: prevCb.font ?? defCb.font,
                    weight: prevCb.weight ?? defCb.weight,
                    size: prevCb.size ?? defCb.size,
                    color: prevCb.color ?? defCb.color,
                  };
                }
                return out;
              });
            }}
          />
          <Checkbox
            label={
              <LabelWithTooltip
                label="Enforce minimum age (restrict verification)"
                tooltip="When enabled, failed checks (wrong date, under age, or unchecked confirmation) keep the popup open, show errors where applicable, and do not record a verified completion. When disabled, visitors can still complete the step to close the popup and record verification regardless of age — use for analytics-style capture only."
              />
            }
            checked={p.restrictVerification !== false}
            onChange={(v) => setPopup((prev) => ({ ...prev, restrictVerification: v }))}
          />
          {normalizePopupVerificationMethod(p.verificationMethod) === "birthdate" ? (
            <BlockStack gap="200">
              <Select
                label="Date format"
                options={DATE_FORMAT_TYPE_OPTIONS}
                value={coerceDateFormatType(p.dob?.dateFormat)}
                onChange={(v) =>
                  setPopup((prev) => ({
                    ...prev,
                    dob: {
                      ...prev.dob,
                      dateFormat: v,
                      dateOrder: dateOrderCommaFromDateFormat(v),
                    },
                  }))
                }
              />
              <TextField
                label={
                  <LabelWithTooltip
                    label="Error message"
                    tooltip="Shown when the date is incomplete or invalid. Use {{minimum_age}} for the minimum age number above."
                  />
                }
                value={p.dob?.errorMessage || ""}
                onChange={(v) => setPopup((prev) => ({ ...prev, dob: { ...prev.dob, errorMessage: v } }))}
                autoComplete="off"
              />
            </BlockStack>
          ) : null}
          {normalizePopupVerificationMethod(p.verificationMethod) === "checkbox" ? (
            <BlockStack gap="200">
              <TextField
                label={
                  <LabelWithTooltip
                    label="Checkbox title"
                    tooltip="Shown next to the checkbox on the storefront and in preview. Use {{minimum_age}} for the minimum age number above."
                  />
                }
                placeholder={DEFAULT_POPUP_CHECKBOX_LABEL}
                value={p.confirmCheckbox?.label || ""}
                onChange={(v) =>
                  setPopup((prev) => ({
                    ...prev,
                    confirmCheckbox: { ...prev.confirmCheckbox, enabled: true, label: v },
                  }))
                }
                autoComplete="off"
              />
              <Text as="p" variant="bodySm" tone="subdued">
                Font, weight, size, and color apply to the checkbox label in preview and on the storefront.
              </Text>
              <InlineStack gap="300" wrap>
                <div style={{ flex: "1 1 140px" }}>
                  <Select
                    label="Checkbox font"
                    options={fontSelectOptions(p.confirmCheckbox?.font)}
                    value={normalizeFontSelectValue(p.confirmCheckbox?.font)}
                    onChange={(v) =>
                      setPopup((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, enabled: true, font: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <Select
                    label="Checkbox weight"
                    options={TEXT_WEIGHT_OPTIONS}
                    value={p.confirmCheckbox?.weight || "400"}
                    onChange={(v) =>
                      setPopup((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, enabled: true, weight: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <FontSizePxField
                    label="Checkbox text size"
                    value={p.confirmCheckbox?.size || "15px"}
                    onChange={(v) =>
                      setPopup((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, enabled: true, size: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <ColorHexField
                    label="Checkbox text color"
                    value={p.confirmCheckbox?.color || "#111827"}
                    onChange={(v) =>
                      setPopup((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, enabled: true, color: v },
                      }))
                    }
                  />
                </div>
              </InlineStack>
            </BlockStack>
          ) : null}
          <Select
            label={
              <LabelWithTooltip
                label="When to show the popup"
                tooltip="Cookie mode remembers verification for a chosen number of days."
              />
            }
            options={[
              { label: "Show every visit until confirmed", value: "every_time" },
              { label: "Remember with a cookie (set duration below)", value: "cookie" },
            ]}
            value={p.popupShow?.mode || "every_time"}
            onChange={(v) =>
              setPopup((prev) => ({ ...prev, popupShow: { ...prev.popupShow, mode: v } }))
            }
          />
          {p.popupShow?.mode === "cookie" ? (
            <BlockStack gap="200">
              <Banner tone="info">
                <p>
                  A cookie is stored in the visitor&apos;s browser so the popup does not repeat until the
                  duration expires.
                </p>
              </Banner>
              <Select
                label="Cookie duration"
                options={cookieDurationSelectOptions(p.popupShow?.cookieDays)}
                value={String(
                  Number.isFinite(Number(p.popupShow?.cookieDays)) && Number(p.popupShow?.cookieDays) > 0
                    ? Number(p.popupShow?.cookieDays)
                    : 30,
                )}
                onChange={(v) =>
                  setPopup((prev) => ({
                    ...prev,
                    popupShow: { ...prev.popupShow, cookieDays: parseInt(v, 10) || 30 },
                  }))
                }
              />
            </BlockStack>
          ) : (
            <Banner tone="info">
              <p>The age gate can appear on each new session until the visitor completes verification.</p>
            </Banner>
          )}
      </BlockStack>

      <Divider />

      <DisplayConditionSection
        displayCriteria={p.displayCriteria}
        onPatch={(patch) =>
          setPopup((prev) => ({
            ...prev,
            displayCriteria: { ...(prev.displayCriteria || {}), ...patch },
          }))
        }
      />

      <Divider />

      <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Logo & display
          </Text>
          <Checkbox
            label="Show logo"
            checked={!!p.logo?.show}
            onChange={(v) => setPopup((prev) => ({ ...prev, logo: { ...prev.logo, show: v } }))}
          />
          {p.logo?.show ? (
            <BlockStack gap="300">
              <Checkbox
                label="Logo square"
                checked={!!p.logo?.square}
                onChange={(v) => setPopup((prev) => ({ ...prev, logo: { ...prev.logo, square: v } }))}
              />
              <ImageUploadField
                label="Logo image"
                helpText="Uploads to Shopify Files; CDN URL is saved (max 4MB). PNG/JPG recommended."
                uploadAlt="Age gate logo"
                value={p.logo?.url || ""}
                onChange={(url) =>
                  setPopup((prev) => ({ ...prev, logo: { ...prev.logo, url } }))
                }
              />
            </BlockStack>
          ) : null}
          <Checkbox
            label="Show privacy policy"
            checked={!!p.privacy?.enabled}
            onChange={(v) =>
              setPopup((prev) => {
                const prevHtml = String(prev.privacy?.html || "").trim();
                return {
                  ...prev,
                  privacy: {
                    ...prev.privacy,
                    enabled: v,
                    ...(v && !prevHtml ? { html: "Privacy policy" } : {}),
                  },
                };
              })
            }
          />
          {p.privacy?.enabled ? (
            <PrivacyPolicyRichTextEditor
              label={
                <LabelWithTooltip
                  label="Privacy policy text"
                  tooltip="Alter the popup privacy policy text appearance"
                />
              }
              value={p.privacy?.html ?? ""}
              onChange={(html) =>
                setPopup((prev) => ({ ...prev, privacy: { ...prev.privacy, html } }))
              }
              placeholder="Privacy policy"
            />
          ) : null}
          {/* Temporarily hidden — monthly analysis emails opt-out
          <Checkbox
            label="I don't want monthly analysis emails"
            checked={!!p.monthlyAnalysisOptOut}
            onChange={(v) => setPopup((prev) => ({ ...prev, monthlyAnalysisOptOut: v }))}
          />
          */}
      </BlockStack>
    </BlockStack>
  );
}

function PopupAccordionTextBody({ p, setPopup }) {
  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">
        Text customization
      </Text>
      <TextField
        label="Title"
        value={p.text?.title?.text || ""}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            text: {
              ...prev.text,
              title: { ...prev.text?.title, text: v },
            },
          }))
        }
        autoComplete="off"
      />
      <InlineStack gap="300" wrap>
        <div style={{ flex: "1 1 140px" }}>
          <Select
            label="Title font"
            options={fontSelectOptions(p.text?.title?.font)}
            value={normalizeFontSelectValue(p.text?.title?.font)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: { ...prev.text, title: { ...prev.text?.title, font: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <Select
            label="Title weight"
            options={TEXT_WEIGHT_OPTIONS}
            value={p.text?.title?.weight || "700"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: { ...prev.text, title: { ...prev.text?.title, weight: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <FontSizePxField
            label="Text size"
            value={p.text?.title?.size || "35px"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: { ...prev.text, title: { ...prev.text?.title, size: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <ColorHexField
            label="Text color"
            value={p.text?.title?.color || "#111827"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: { ...prev.text, title: { ...prev.text?.title, color: v } },
              }))
            }
          />
        </div>
      </InlineStack>
      <TextField
        label="Description"
        multiline={3}
        value={p.text?.description?.text || ""}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            text: {
              ...prev.text,
              description: { ...prev.text?.description, text: v },
            },
          }))
        }
        autoComplete="off"
      />
      <InlineStack gap="300" wrap>
        <div style={{ flex: "1 1 140px" }}>
          <Select
            label="Description font"
            options={fontSelectOptions(p.text?.description?.font)}
            value={normalizeFontSelectValue(p.text?.description?.font)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: {
                  ...prev.text,
                  description: { ...prev.text?.description, font: v },
                },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <Select
            label="Description weight"
            options={TEXT_WEIGHT_OPTIONS}
            value={p.text?.description?.weight || "400"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: {
                  ...prev.text,
                  description: { ...prev.text?.description, weight: v },
                },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <FontSizePxField
            label="Description size"
            value={p.text?.description?.size || "16px"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: {
                  ...prev.text,
                  description: { ...prev.text?.description, size: v },
                },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <ColorHexField
            label="Description color"
            value={p.text?.description?.color || "#4B5563"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                text: {
                  ...prev.text,
                  description: { ...prev.text?.description, color: v },
                },
              }))
            }
          />
        </div>
      </InlineStack>
    </BlockStack>
  );
}

function PopupAccordionButtonBody({ p, setPopup }) {
  return (
    <BlockStack gap="300">
      <Text as="p" variant="bodySm" fontWeight="semibold">
        Accept
      </Text>
      <ButtonFields prefix="accept" btn={p.buttons?.accept} setPopup={setPopup} />
      <Text as="p" variant="bodySm" fontWeight="semibold">
        Reject
      </Text>
      <ButtonFields prefix="reject" btn={p.buttons?.reject} setPopup={setPopup} />
      <TextField
        label="Reject redirect URL"
        value={p.buttons?.reject?.redirectUrl || ""}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            buttons: {
              ...prev.buttons,
              reject: { ...prev.buttons?.reject, redirectUrl: v },
            },
          }))
        }
        autoComplete="off"
      />
    </BlockStack>
  );
}

function PopupAccordionBackgroundBody({ p, setPopup }) {
  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">
        Popup appearance
      </Text>
      <Select
        label="Animation"
        options={ANIMATIONS}
        value={normalizePopupAnimation(p.popupStyle?.animation)}
        onChange={(v) =>
          setPopup((prev) => ({ ...prev, popupStyle: { ...prev.popupStyle, animation: v } }))
        }
      />
      <InlineStack gap="300" wrap>
        <div style={{ flex: "1 1 120px" }}>
          <TextField
            label="Width (px)"
            type="number"
            value={String(p.popupStyle?.width ?? 600)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, width: parseInt(v, 10) || 0 },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <TextField
            label={
              <LabelWithTooltip
                label="Height"
                tooltip='Number (px) or "auto"'
              />
            }
            value={p.popupStyle?.height != null ? String(p.popupStyle.height) : "auto"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: {
                  ...prev.popupStyle,
                  height: v === "" || v.toLowerCase() === "auto" ? "auto" : v,
                },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <TextField
            label="Padding Y"
            type="number"
            value={String(p.popupStyle?.paddingY ?? 24)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, paddingY: parseInt(v, 10) || 0 },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <TextField
            label="Padding X"
            type="number"
            value={String(p.popupStyle?.paddingX ?? 24)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, paddingX: parseInt(v, 10) || 0 },
              }))
            }
            autoComplete="off"
          />
        </div>
      </InlineStack>
      <ColorHexField
        label="Background color"
        value={p.popupStyle?.backgroundColor || "#FFFFFF"}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            popupStyle: { ...prev.popupStyle, backgroundColor: v },
          }))
        }
      />
      <RangeSlider
        label="Inner popup background opacity"
        value={(() => {
          const v = p.popupStyle?.backgroundOpacity;
          if (typeof v === "number" && Number.isFinite(v)) {
            return Math.min(100, Math.max(0, v));
          }
          const n = parseInt(String(v ?? ""), 10);
          if (Number.isFinite(n)) return Math.min(100, Math.max(0, n));
          return DEFAULT_POPUP_BACKGROUND_OPACITY;
        })()}
        min={0}
        max={100}
        onChange={(v) =>
          setPopup((prev) => ({ ...prev, popupStyle: { ...prev.popupStyle, backgroundOpacity: v } }))
        }
        output
      />
      <Checkbox
        label="Enable background image"
        checked={!!p.popupStyle?.backgroundImageEnabled}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            popupStyle: { ...prev.popupStyle, backgroundImageEnabled: v },
          }))
        }
      />
      {p.popupStyle?.backgroundImageEnabled ? (
        <ImageUploadField
          label="Background image (inside popup)"
          helpText="Maximum file size: 4MB."
          uploadAlt="Age gate popup background"
          value={p.popupStyle?.backgroundImageUrl || ""}
          onChange={(url) =>
            setPopup((prev) => ({
              ...prev,
              popupStyle: { ...prev.popupStyle, backgroundImageUrl: url },
            }))
          }
        />
      ) : null}
      <InlineStack gap="300" wrap>
        <div style={{ flex: "1 1 100px" }}>
          <TextField
            label="Border width (px)"
            type="number"
            value={String(p.popupStyle?.borderWidth ?? 0)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, borderWidth: parseInt(v, 10) || 0 },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 100px" }}>
          <TextField
            label="Border radius (px)"
            type="number"
            value={String(p.popupStyle?.borderRadius ?? 12)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, borderRadius: parseInt(v, 10) || 0 },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <ColorHexField
            label="Border color"
            value={p.popupStyle?.borderColor || "#E5E7EB"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                popupStyle: { ...prev.popupStyle, borderColor: v },
              }))
            }
          />
        </div>
      </InlineStack>
      <ColorHexField
        label="Outer overlay color"
        value={p.popupStyle?.outerColor || "#000000"}
        onChange={(v) =>
          setPopup((prev) => ({ ...prev, popupStyle: { ...prev.popupStyle, outerColor: v } }))
        }
      />
      <RangeSlider
        label="Outer layer opacity"
        value={p.popupStyle?.outerOpacity ?? DEFAULT_POPUP_OUTER_OPACITY}
        min={0}
        max={100}
        onChange={(v) => {
          const raw = Array.isArray(v) ? v[0] : v;
          const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
          const next = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : DEFAULT_POPUP_OUTER_OPACITY;
          setPopup((prev) => ({
            ...prev,
            popupStyle: { ...prev.popupStyle, outerOpacity: next },
          }));
        }}
        output
      />
      <Checkbox
        label="Enable outer background image"
        checked={!!p.popupStyle?.outerImageEnabled}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            popupStyle: { ...prev.popupStyle, outerImageEnabled: v },
          }))
        }
      />
      {p.popupStyle?.outerImageEnabled ? (
        <ImageUploadField
          label="Outer image (behind popup, on store)"
          helpText="Maximum file size: 4MB."
          uploadAlt="Age gate outer overlay"
          value={p.popupStyle?.outerImageUrl || ""}
          onChange={(url) =>
            setPopup((prev) => ({
              ...prev,
              popupStyle: { ...prev.popupStyle, outerImageUrl: url },
            }))
          }
        />
      ) : null}
    </BlockStack>
  );
}

function PopupAccordionCssBody({ p, setPopup }) {
  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">
        Advanced (developers)
      </Text>
      <TextField
        label="Custom CSS"
        multiline={4}
        value={p.advanced?.customCss || ""}
        onChange={(v) =>
          setPopup((prev) => ({ ...prev, advanced: { ...prev.advanced, customCss: v } }))
        }
        autoComplete="off"
      />
      <TextField
        label="Custom script"
        multiline={4}
        value={p.advanced?.customScript || ""}
        onChange={(v) =>
          setPopup((prev) => ({ ...prev, advanced: { ...prev.advanced, customScript: v } }))
        }
        autoComplete="off"
      />
    </BlockStack>
  );
}

function PopupSection({ setPopup, p, popupEnabled, setConfig }) {
  /** `null` = all accordion panels closed */
  const [popupSubTab, setPopupSubTab] = useState(/** @type {string | null} */ ("info"));

  const renderPopupAccordionPanel = (id) => {
    switch (id) {
      case "info":
        return <PopupAccordionInfoBody p={p} setPopup={setPopup} />;
      case "text":
        return <PopupAccordionTextBody p={p} setPopup={setPopup} />;
      case "button":
        return <PopupAccordionButtonBody p={p} setPopup={setPopup} />;
      case "background":
        return <PopupAccordionBackgroundBody p={p} setPopup={setPopup} />;
      case "css":
        return <PopupAccordionCssBody p={p} setPopup={setPopup} />;
      default:
        return null;
    }
  };

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm">
              Storefront popup
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Enable or disable the popup for this template.
            </Text>
          </BlockStack>
          <Checkbox
            label="Enable age verification popup on the storefront"
            checked={popupEnabled}
            onChange={(v) => setConfig((c) => ({ ...c, popupEnabled: v }))}
          />
        </BlockStack>
      </Card>

      <BlockStack gap="150">
        {POPUP_EDITOR_SUB_TABS.map((t) => {
          const active = popupSubTab === t.id;
          return (
            <Card key={t.id} padding="0">
              <BlockStack gap="0">
                <Box paddingInline="300" paddingBlock="200">
                  <button
                    type="button"
                    onClick={() => setPopupSubTab((cur) => (cur === t.id ? null : t.id))}
                    aria-expanded={active}
                    style={{
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      textAlign: "left",
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight={active ? "semibold" : "regular"}>
                        {t.label}
                      </Text>
                      <Text as="span" variant="bodyMd" tone="subdued">
                        {active ? "−" : "+"}
                      </Text>
                    </InlineStack>
                  </button>
                </Box>
                {active ? (
                  <>
                    <Divider />
                    <Box padding="400">{renderPopupAccordionPanel(t.id)}</Box>
                  </>
                ) : null}
              </BlockStack>
            </Card>
          );
        })}
      </BlockStack>
    </BlockStack>
  );
}
function ButtonFields({ prefix, btn, setPopup }) {
  const b = btn || {};
  return (
    <BlockStack gap="200">
      <TextField
        label={
          <LabelWithTooltip
            label="Text"
            tooltip='Use {{minimum_age}} for the popup “Verify age” number (e.g. No, I am under {{minimum_age}}).'
          />
        }
        value={b.text || ""}
        onChange={(v) =>
          setPopup((prev) => ({
            ...prev,
            buttons: {
              ...prev.buttons,
              [prefix]: { ...prev.buttons?.[prefix], text: v },
            },
          }))
        }
        autoComplete="off"
      />
      <InlineStack gap="200" wrap>
        <div style={{ flex: "1 1 100px" }}>
          <TextField
            label="Border radius"
            type="number"
            suffix="Px"
            value={String(b.borderRadius ?? 8)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: {
                  ...prev.buttons,
                  [prefix]: { ...prev.buttons?.[prefix], borderRadius: parseInt(v, 10) || 0 },
                },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 100px" }}>
          <TextField
            label="Border width"
            type="number"
            suffix="Px"
            value={String(b.borderWidth ?? 1)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: {
                  ...prev.buttons,
                  [prefix]: { ...prev.buttons?.[prefix], borderWidth: parseInt(v, 10) || 0 },
                },
              }))
            }
            autoComplete="off"
          />
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <Select
            label="Font"
            options={fontSelectOptions(b.font)}
            value={normalizeFontSelectValue(b.font)}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], font: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <Select
            label="Text weight"
            options={TEXT_WEIGHT_OPTIONS}
            value={String(b.weight || "600")}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], weight: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 100px" }}>
          <FontSizePxField
            label="Text size"
            value={b.size || "16px"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], size: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <ColorHexField
            label="Text color"
            value={b.color || "#fff"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], color: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <ColorHexField
            label="Background"
            value={b.background || "#111"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], background: v } },
              }))
            }
          />
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <ColorHexField
            label="Border color"
            value={b.borderColor || "#111"}
            onChange={(v) =>
              setPopup((prev) => ({
                ...prev,
                buttons: { ...prev.buttons, [prefix]: { ...prev.buttons?.[prefix], borderColor: v } },
              }))
            }
          />
        </div>
      </InlineStack>
    </BlockStack>
  );
}

function CheckoutSection({
  config,
  setConfig,
  setCheckout,
  ch,
  shopPlanLabel,
  showCheckoutPlanRestriction,
  checkoutPreviewValidationError,
  onCheckoutPreviewValidationChange,
}) {
  const method = normalizeCheckoutVerificationMethod(ch.verificationMethod);
  const checkoutVerificationChoices = useMemo(
    () => [
      {
        value: "dob",
        label: (
          <LabelWithTooltip
            label="Date of birth"
            tooltip="Year, month, and day fields at checkout."
          />
        ),
      },
      {
        value: "checkbox",
        label: (
          <LabelWithTooltip
            label="Checkbox only"
            tooltip="Single checkbox (no date fields)."
          />
        ),
      },
    ],
    [],
  );
  const checkoutEditorUrl = getShopifyAdminCheckoutEditorUrl();
  const planLocksCheckout = showCheckoutPlanRestriction;
  const previewBlockedCheckoutCheckbox = (
    <Checkbox
      label={
        <LabelWithTooltip
          label="Preview validation error styling"
          tooltip="Shows the highlighted age block when the date is invalid or under age (checkout UI does not stop payment)."
        />
      }
      checked={!!checkoutPreviewValidationError}
      onChange={onCheckoutPreviewValidationChange}
      disabled={planLocksCheckout}
    />
  );
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="300" blockAlign="center" wrap>
            <Text as="h3" variant="headingSm">
              Checkout verification
            </Text>
            {showCheckoutPlanRestriction ? <Badge tone="warning">Plus required</Badge> : null}
          </InlineStack>
          {showCheckoutPlanRestriction ? (
            <Banner tone="warning">
              <p>
                Blocking checkout until age is verified needs <strong>Shopify Plus</strong> (checkout UI extensions),
                or a <strong>development</strong> store. Your plan: <strong>{shopPlanLabel}</strong>. Checkout options
                below are read-only; save the template from the bar above to keep other changes (for example popup
                settings).
              </p>
            </Banner>
          ) : null}
          <Text as="p" variant="bodySm" tone="subdued">
            Country and language for this template are set in the section above; they apply to checkout as well.
          </Text>
          <Checkbox
            label="Checkout verification on"
            checked={!!config.checkoutEnabled}
            disabled={planLocksCheckout}
            onChange={(v) => {
              setConfig((c) => ({ ...c, checkoutEnabled: v }));
              if (!v) {
                setCheckout((prev) => ({ ...prev, ageVerificationBlockEnabled: false }));
              }
            }}
          />
          {!config.checkoutEnabled ? (
            <Banner tone="warning">
              <p>The checkout verification step is currently off for this template.</p>
            </Banner>
          ) : null}
          <Text as="p" variant="bodySm" tone="subdued">
            When enabled, buyers see your age verification step at checkout (theme integration required).
          </Text>
          {config.checkoutEnabled ? (
            <BlockStack gap="200">
              <Button
                variant="primary"
                disabled={planLocksCheckout}
                onClick={() => {
                  window.open(checkoutEditorUrl, "_blank", "noopener,noreferrer");
                }}
              >
                Add age verification block
              </Button>
            </BlockStack>
          ) : null}

          <Divider />

          <Text as="h3" variant="headingSm">
            Verification settings
          </Text>

          <ChoiceList
            title="Verification method"
            choices={checkoutVerificationChoices}
            selected={[method]}
            disabled={planLocksCheckout}
            onChange={(selected) =>
              setCheckout({
                verificationMethod: normalizeCheckoutVerificationMethod(selected[0]),
              })
            }
          />

          <TextField
            label={
              <LabelWithTooltip
                label="Verification message"
                tooltip="Shown on checkout above the date fields or checkbox. Use {{minimum_age}} for the minimum age from the rule below."
              />
            }
            value={ch.instructionMessage ?? ""}
            disabled={planLocksCheckout}
            onChange={(v) =>
              setCheckout((prev) => ({
                ...prev,
                instructionMessage: v,
              }))
            }
            autoComplete="off"
          />

          {method === "dob" ? (
            <BlockStack gap="300">
              <Text as="h4" variant="headingSm">
                Date of birth
              </Text>
              <Select
                label="Date format"
                options={DATE_FORMAT_TYPE_OPTIONS}
                value={coerceDateFormatType(ch.dob?.dateFormat)}
                disabled={planLocksCheckout}
                onChange={(v) =>
                  setCheckout((prev) => ({
                    ...prev,
                    dob: {
                      ...prev.dob,
                      dateFormat: v,
                      dateOrder: dateOrderCommaFromDateFormat(v),
                    },
                  }))
                }
              />
              <TextField
                label={
                  <LabelWithTooltip
                    label="Error message"
                    tooltip="Shown when the date is invalid, under age, or the buyer tries to continue without completing verification. Use {{minimum_age}} in the text."
                  />
                }
                value={ch.dob?.errorMessage || ""}
                placeholder='You must be {{minimum_age}} or older. Please enter a valid date of birth.'
                disabled={planLocksCheckout}
                onChange={(v) =>
                  setCheckout((prev) => ({ ...prev, dob: { ...prev.dob, errorMessage: v } }))
                }
                autoComplete="off"
              />
              {previewBlockedCheckoutCheckbox}
            </BlockStack>
          ) : null}

          {method === "checkbox" ? (
            <BlockStack gap="300">
              <Text as="h4" variant="headingSm">
                Checkbox only
              </Text>
              <TextField
                label={
                  <LabelWithTooltip
                    label="Checkbox label"
                    tooltip='Text next to the checkbox on checkout. Use {{minimum_age}} for the checkout minimum age (default copy: I confirm I am {{minimum_age}}+).'
                  />
                }
                value={ch.confirmCheckbox?.label ?? ""}
                placeholder={DEFAULT_POPUP_CHECKBOX_LABEL}
                disabled={planLocksCheckout}
                onChange={(v) =>
                  setCheckout((prev) => ({
                    ...prev,
                    confirmCheckbox: { ...prev.confirmCheckbox, label: v },
                  }))
                }
                autoComplete="off"
              />
              <Text as="p" variant="bodySm" tone="subdued">
                Font, weight, size, and color apply to the checkout checkbox label in preview and checkout.
              </Text>
              <InlineStack gap="300" wrap>
                <div style={{ flex: "1 1 140px" }}>
                  <Select
                    label="Checkbox font"
                    options={fontSelectOptions(ch.confirmCheckbox?.font)}
                    value={normalizeFontSelectValue(ch.confirmCheckbox?.font)}
                    disabled={planLocksCheckout}
                    onChange={(v) =>
                      setCheckout((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, font: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <Select
                    label="Checkbox weight"
                    options={TEXT_WEIGHT_OPTIONS}
                    value={ch.confirmCheckbox?.weight || "400"}
                    disabled={planLocksCheckout}
                    onChange={(v) =>
                      setCheckout((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, weight: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <FontSizePxField
                    label="Checkbox text size"
                    value={ch.confirmCheckbox?.size || "15px"}
                    disabled={planLocksCheckout}
                    onChange={(v) =>
                      setCheckout((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, size: v },
                      }))
                    }
                  />
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <ColorHexField
                    label="Checkbox text color"
                    value={ch.confirmCheckbox?.color || "#111827"}
                    disabled={planLocksCheckout}
                    onChange={(v) =>
                      setCheckout((prev) => ({
                        ...prev,
                        confirmCheckbox: { ...prev.confirmCheckbox, color: v },
                      }))
                    }
                  />
                </div>
              </InlineStack>
              {previewBlockedCheckoutCheckbox}
            </BlockStack>
          ) : null}

          <Divider />

          <CheckoutTargetSection
            disabled={planLocksCheckout}
            target={ch.target}
            onPatch={(patch) =>
              setCheckout((prev) => ({
                ...prev,
                target: normalizeCheckoutTarget({ ...(prev.target || {}), ...patch }),
              }))
            }
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
